import React, { useEffect, useState } from "react";
import { ImageType, INoticeComponent } from "interfaces/sw/SWInterfaces";
import BlockSpinner from "components/common/BlockSpinner";
import "./NoticeImageEditor.scoped.scss";
import { useDispatch } from "react-redux";
import { downloadNoticeTimeImage, getNoticeImageUrl, setTimeImageData } from "store/manageSW/manageSWActions";
import { ImageDataDestinations, ITimeImageData } from "store/manageSW/manageSWTypes";
import DeleteIcon from "media/icons/dls/delete.svg";
import useSelector from "store/useSelector";
import AddTimeImageModal from "../AddTimeImageModal";
import DownloadIcon from "media/icons/dls/download.svg";
import { v4 as uuidv4 } from "uuid";
import { getMediaNumberFromLabel } from "utilities/fileUtilities";

interface INoticeTimeImageEditorProps {
    notice: INoticeComponent,
    stepGuid?: string,
    allowEdit?: boolean,
    swGuid: string,
    swVersion: number,
    imageDataDestination: ImageDataDestinations,
    executionPreview?: boolean,
}

const NoticeTimeImageEditor: React.FC<INoticeTimeImageEditorProps> = ({
    notice,
    stepGuid,
    allowEdit,
    swGuid,
    swVersion,
    imageDataDestination,
    executionPreview
}) => {
    const { fileUploadOperation, timeImageData } = useSelector(store => store.manageSW);
    const dispatch = useDispatch();
    const [openTimeModal, setOpenTimeModal] = useState(false);

    useEffect(() => {
        if (!notice.image.filename
            || notice.image.imageType === ImageType.Normal
            || notice.image.blobData?.isLoading
            || notice.image.filename === notice.image.blobData?.filename
            || fileUploadOperation?.isWorking) {
            // Blob url already loaded. Ignore.
            return;
        }

        dispatch(getNoticeImageUrl({
            swGuid,
            swVersion,
            noticeGuid: notice.guid,
            stepGuid,
            filename: notice.image.filename,
            destination: imageDataDestination,
            imageType: notice.image.imageType,
        }));
    }, [notice.image, notice.guid, stepGuid, swGuid, swVersion, imageDataDestination, fileUploadOperation, dispatch]);

    let img: React.ReactElement | undefined;

    if (notice.image.blobData?.isLoading) {
        img = (
            <BlockSpinner />
        );
    } else if (notice.image.blobData?.errorMessage) {
        img = (
            <label className="error">
                {notice.image.blobData?.errorMessage}
            </label>
        );
    } else if (notice.image.blobData?.blobUrl && notice.image.imageType === ImageType.Time) {
        img = (
            <>
                <img
                    src={notice.image.blobData?.blobUrl}
                    alt="Selected"
                    className="chosen-image"
                    style={{width: "400px"}}
                />
                {
                    !executionPreview &&
                    <img
                        src={DownloadIcon}
                        className="icon-small download-button"
                        alt="Download"
                        title="Download Image"
                        onClick={() => downloadTimeImage()}
                    />
                }
                {allowEdit &&
                    <img
                        src={DeleteIcon}
                        alt="Delete"
                        title="Delete Image"
                        className="delete-button icon-small"
                        onClick={(e) => onDeleteImage(e)}
                    />
                }
            </>
        );
    }

    const downloadTimeImage = async () => {
        let timeImage = notice.image;
        const image = await fetch(timeImage.blobData?.blobUrl!);
        const imageBlob = await image.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(imageBlob);
        link.download = `${uuidv4()}_${getMediaNumberFromLabel(timeImage.filename)}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const openCloseModal = () => {
        setOpenTimeModal(prevState => !prevState);
        dispatch(setTimeImageData({
            imageNumber: 0,
            link: "",
            label: "",
        }));
    }

    const onDeleteImage = (_event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        dispatch(downloadNoticeTimeImage({
            swGuid,
            swVersion: 0,
            imageNumber: 0,
            link: "",
            label: "",
            noticeGuid: notice.guid,
            stepGuid
        }));
    }

    const downloadImage = (data: ITimeImageData) => {
        dispatch(downloadNoticeTimeImage({
            swGuid,
            swVersion: 0,
            label: data.label,
            link: data.link,
            imageNumber: data.imageNumber,
            noticeGuid: notice.guid,
            stepGuid
        }));
    }

    return (
        <>
            <div className="image-wrapper">
                {img}
            </div>
            {
                openTimeModal &&
                <AddTimeImageModal
                    onModalClick={openCloseModal}
                    timeImageData={timeImageData}
                    uploadDownloadImage={downloadImage}
                />
            }

            {allowEdit &&
                <>
                    <div
                        className="controls"
                    >
                        <button
                            className="primary-button choose-image"
                            onClick={openCloseModal}
                        >
                            {notice.image.filename ? "Change" : "Choose"} TIME Image
                        </button>
                    </div>
                </>
            }
        </>
    );
}

export default NoticeTimeImageEditor;
